import React, { useState } from "react";
import { matchPath, withRouter } from "react-router";
import { change, destroy, submit } from "redux-form";
import { connect, useSelector } from "react-redux";
import Moment from "moment";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { createCampaign, editCampaign, getCampaign } from "../../../action_managers/campaigns";
import UploadModalActionManager from "../../../action_managers/upload_modal";
import { uploadModalProgress } from "../../../actions/upload_modal";
import { hideModal } from "../../../actions/modal";
import "react-quill/dist/quill.snow.css";
import "./campaign_editor.scss";
import { useComponentDidMount } from "../../../lib/hook";
import { campaignEditorFormName, uploadAudio } from "./campaign_editor_helpers";
import { AdFormat } from "./ad_format";
import { CampaignDetailsEditor } from "./campaign_details_editor";
import { SummaryPage } from "./summary_page";
import { MultiStepWithNav } from "../multi_step_modals/multi_step_modal_with_nav";
import { CampaignStyleHostRead } from "../../../constants/campaigns";
import { showWarning } from "../../../actions/app";
import ProductSamples from "./product_samples";
import { DistributionReach } from "./distribution_reach";
import { validateCampaign } from "../../../lib/validate-campaign";
import filter from "lodash/filter";

const CampaignEditor = props => {
  const [pageNumber, updatePageNum] = useState(0);

  const getCampaignUUID = () =>
    get(
      matchPath(window.location.pathname, "/campaigns/:campaignUUID/edit"),
      "params.campaignUUID"
    ) ||
    // this is a hack because we launch this from two different routes, each with different background. I hate modals
    get(
      matchPath(window.location.pathname, "/campaigns/:campaignUUID/editing"),
      "params.campaignUUID"
    );
  const getCampaign = () => get(props.campaigns, getCampaignUUID());

  useComponentDidMount(() => {
    if (getCampaignUUID() && !getCampaign()) {
      props.getCampaign(getCampaignUUID());
    }
  });

  const formValues = useSelector(state => state.form[campaignEditorFormName]?.values);

  const onUpdatePage = async newPageNumber => {
    const campaignUpdates = {
      ...formValues,
      redemptionCodes: formValues?.redemptionCodeStringForm
        ? formValues.redemptionCodeStringForm.split(/[\s,\r\n]+/).filter(a => a)
        : [],
      style: formValues.style || undefined,
    };

    if (campaignUpdates.website && campaignUpdates.website.indexOf("http") !== 0) {
      campaignUpdates.website = "http://" + campaignUpdates.website;
      props.changeForm("website", campaignUpdates.website);
    }

    let resp;
    if (newPageNumber === 3 && formValues.style === "campaignAudio" && formValues.contentFile) {
      resp = await uploadAudio(props, formValues);
      if (resp.status === 200) {
        campaignUpdates.contentMediaFileUUID = get(resp, ["mediaFileUUIDs", "128", "uuid"]);
      }
      campaignUpdates.contentFile = null;
    }

    if (pageNumber === 0 && !getCampaignUUID()) {
      resp = await props.createCampaign(campaignUpdates);
      if (resp.status !== 200) return;
      // let the backend decide the default values, and then change the frontend form based on it
      // this is a little hacky, but i can't figure out how to do it otherwise
      ["preRoll", "postRoll", "midRoll"].forEach(field => {
        const fieldName = "targetingOptions." + field;
        props.changeForm(fieldName, get(resp.json, fieldName));
      });
      props.history.push(`/campaigns/${resp.json.uuid}/edit`);
    } else {
      resp = await props.editCampaign({ uuid: getCampaignUUID(), ...campaignUpdates });
      if (resp.status !== 200) return;
    }

    updatePageNum(newPageNumber);
  };

  const campaign = getCampaign();
  const initialValues = {
    ...campaign,
    ...(get(campaign, "startsAt") && {
      timeline: [Moment.unix(campaign.startsAt), Moment.unix(campaign.estimatedEndsAt || 0)],
    }),
    ...(campaign?.redemptionCodes && {
      redemptionCodeStringForm: Object.keys(campaign.redemptionCodes).join(", "),
    }),
    distributionType: campaign?.distributionType || "continuous",
  };

  const navWrapperConfigs = [
    {
      navOption: "Campaign Details",
      Content: <CampaignDetailsEditor onSubmit={() => {}} initialValues={initialValues} />,
      subTitle:
        "Tell us about the product or service you’re promoting. When you invite podcasts to your campaign, this is the information they will see.",
      disabled: !formValues?.name,
    },
    {
      navOption: "Product Samples",
      Content: <ProductSamples initialValues={initialValues} />,
      subTitle: "Let the Podcasters know if and how you’ll provide samples of your product.",
    },
    {
      navOption: "Ad Format",
      subTitle: "How would you like your promotion heard?",
      Content: <AdFormat onSubmit={() => {}} initialValues={initialValues} />,
      validator: () => {
        const { style, promoCodeType } = formValues;
        if (style === CampaignStyleHostRead && !promoCodeType) {
          props.showWarning("Please select an option for promotion code");
          return false;
        }
        return true;
      },
    },
    {
      navOption: "Distribution Reach",
      subTitle: "Choose what kind of distribution reach you’d like for this promotion.",
      Content: (
        <DistributionReach
          campaign={campaign}
          onSubmit={() => {}}
          initialValues={initialValues}
          formValues={formValues}
        />
      ),
      validator: () => {
        const { targetingOptions } = formValues;
        if (filter(targetingOptions, option => option === true).length < 1) {
          props.showWarning("Please select at least one ad placement");
          return false;
        }
        return true;
      },
    },
    {
      navOption: "Summary",
      subTitle:
        "Here’s a summary of your campaign. If everything looks good, publish your campaign. Then, invite the podcasts that you think would be a good fit for your campaign.",
      Content: <SummaryPage campaign={campaign} updatePage={updatePageNum} />,
      submitText: validateCampaign(campaign)[0] ? "Done" : "Save as Draft",
    },
  ];

  const exitModal = campaignUUID => {
    props.destroyForm();
    if (campaignUUID) {
      props.history.push(`/campaigns/${campaignUUID}`);
    } else {
      props.history.push("/campaigns");
    }
    props.closeModal();
  };
  const commonSettings = {
    showContent: true,
    title: (pageNumber !== 0 && formValues?.name) || "New Campaign",
    columns: 7,
    offset: 4,
  };

  return (
    <MultiStepWithNav
      backText="Save & Back"
      nextText="Save & Continue"
      commonConfigs={commonSettings}
      configs={navWrapperConfigs}
      pageNumber={pageNumber}
      isEdit={props.isEdit}
      onUpdatePage={onUpdatePage}
      isLoading={props.mediaFileUploadIsLoading || (props.isEdit && isEmpty(campaign))}
      close={() => exitModal(getCampaignUUID())}
    />
  );
};

export default connect(
  state => ({
    campaigns: state.campaigns.campaigns,
    isLoading: state.campaigns.isLoading,
    mediaFileUploadIsLoading: state.mediaFileUpload.isLoading,
    mediaFile: state.mediaFiles,
  }),
  {
    submitForm: () => submit(campaignEditorFormName),
    createCampaign,
    editCampaign,
    getCampaign,
    hideModal,
    fileUpload: (file, fileName, progressCallback) =>
      new UploadModalActionManager({
        file,
        fileName,
        progressCallback,
      }).run(),
    destroyForm: () => destroy(campaignEditorFormName),
    uploadProgress: progress => uploadModalProgress(progress),
    changeForm: (field, value) => change(campaignEditorFormName, field, value),
    showWarning,
  }
)(withRouter(CampaignEditor));
